import React, { useState, useEffect } from 'react';
import TimeSeriesChart from '@/components/shared/TimeSeriesChart';
import { subHours } from 'date-fns';
import { apiService } from '@/utils/api';
import { useAnalytics } from './AnalyticsContext';
import { getOrpColor } from '@/utils/colorUtils';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';

interface TimeSeriesPoint {
  timestamp: string;
  value: number;
}

interface TimeSeriesData {
  metric: string;
  points: TimeSeriesPoint[];
}

interface DesiredChemistryResponse {
  desired_ph: number;
  desired_orp: number;
}

const OrpValuePage: React.FC = () => {
  // Get device ID from context
  const { deviceId } = useAnalytics();
  const { mode } = useSelector((state: RootState) => state.theme);
  
  // Default settings
  const [endTimeState, setEndTimeState] = useState<Date>(new Date());
  const [startTimeState, setStartTimeState] = useState<Date>(subHours(new Date(), 24));
  
  const [interval, setInterval] = useState<string>('15m');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentOrpValue, setCurrentOrpValue] = useState<number>(0);
  const [averageOrpValue, setAverageOrpValue] = useState<number>(0);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData[]>([]);
  const [desiredOrp, setDesiredOrp] = useState<number | null>(null);
  
  // Format as ISO strings with UTC timezone
  const formattedStartTime = startTimeState.toISOString();
  const formattedEndTime = endTimeState.toISOString();
  
  // Fetch ORP data when device ID or time parameters change
  useEffect(() => {
    if (!deviceId) return;
    
    const fetchOrpData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const params = {
          device_id: deviceId,
          metrics: ['orp_value'],
          start_time: formattedStartTime,
          end_time: formattedEndTime,
          interval: interval
        };
        
        const data = await apiService.post<TimeSeriesData[]>('/api/v1/sensor-data/time-series', params);
        
        // Reset states when data is empty or invalid
        if (!data || data.length === 0 || data[0].points.length === 0) {
          setCurrentOrpValue(0);
          setAverageOrpValue(0);
          setTimeSeriesData([]);
          return;
        }
        
        // Get latest ORP value as current value
        const latestPoint = data[0].points[data[0].points.length - 1];
        setCurrentOrpValue(latestPoint.value);
        
        // Calculate average ORP value
        const sum = data[0].points.reduce((acc: number, point: TimeSeriesPoint) => acc + point.value, 0);
        const avg = sum / data[0].points.length;
        setAverageOrpValue(parseFloat(avg.toFixed(0)));
        
        // Save time series data for chart
        setTimeSeriesData(data);
      } catch (err) {
        console.error('Error fetching ORP data:', err);
        setError('Failed to fetch ORP data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrpData();
  }, [deviceId, formattedStartTime, formattedEndTime, interval]);

  useEffect(() => {
    if (!deviceId) {
      setDesiredOrp(null);
      return;
    }

    const fetchDesiredOrp = async () => {
      try {
        const response = await apiService.get<DesiredChemistryResponse>(`/api/v1/iot-shadow/device/${deviceId}/desiredChemistry`);
        const nextDesiredOrp = response.desired_orp;
        setDesiredOrp(Number.isFinite(nextDesiredOrp) ? nextDesiredOrp : null);
      } catch (err) {
        console.error('Error fetching desired ORP:', err);
      }
    };

    fetchDesiredOrp();
  }, [deviceId]);
  
  const handleIntervalChange = (newInterval: string) => {
    setInterval(newInterval);
  };
  
  const handleDateRangeChange = (newStartTime: string, newEndTime: string) => {
    // The received times are already in UTC format, so we need to convert them to local time
    const startDate = new Date(newStartTime);
    const endDate = new Date(newEndTime);
    
    // No need to adjust for timezone since Date constructor already handles UTC 'Z' suffix
    setStartTimeState(startDate);
    setEndTimeState(endDate);
  };
  
  const currentOrpColor = getOrpColor(currentOrpValue);
  const averageOrpColor = getOrpColor(averageOrpValue);
  const desiredOrpAnnotations = desiredOrp && desiredOrp > 0 ? [
    {
      y: desiredOrp - 15,
      y2: desiredOrp + 15,
      borderColor: '#10b981',
      fillColor: '#10b981',
      opacity: 0.12
    },
    {
      y: desiredOrp,
      borderColor: '#10b981',
      strokeDashArray: 6,
      label: {
        text: `Desired ORP: ${desiredOrp} mV`,
        borderColor: '#10b981',
        style: {
          color: '#ffffff',
          background: '#10b981',
        },
      },
    },
  ] : undefined;
  
  return (
    <div className="page-container relative h-full flex flex-auto flex-col px-4 sm:px-6 md:px-8 py-4 sm:py-6 md:px-8">
      <div className="flex flex-col gap-4 h-full">
        <div className="lg:flex items-center justify-between mb-4 gap-3">
          <div className="mb-4 lg:mb-0">
            <h3 className={classNames(
              "text-lg font-semibold",
              {
                'text-gray-900': mode === 'light',
                'text-gray-200': mode === 'dark'
              }
            )}>ORP Value</h3>
            <p className={classNames(
              "mt-1 text-sm",
              {
                'text-gray-500': mode === 'light',
                'text-gray-400': mode === 'dark'
              }
            )}>Monitor your device's oxidation-reduction potential</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-4">
          <div className="card card-border" role="presentation">
            <div className="card-body">
              <h6 className="font-semibold mb-4 text-sm">Current ORP</h6>
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-bold">
                    <span>{currentOrpValue} mV</span>
                  </h3>
                  <p>ORP Value</p>
                  {/* <p>Last updated: <span className="font-semibold">{format(new Date(), 'HH:mm:ss')}</span></p> */}
                </div>
                {currentOrpValue !== 0 && (
                  <div className="relative group">
                    <div 
                      className="w-8 h-8 rounded-full border border-gray-200" 
                      style={{ backgroundColor: currentOrpColor }}
                    />
                    <div className="absolute bottom-full right-0 mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10">
                      <p>ORP value: {currentOrpValue} mV</p>
                      <p>Range: 0-1400 mV</p>
                      <p>Ideal range: 672-728 mV (48-52%)</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="card card-border" role="presentation">
            <div className="card-body">
              <h6 className="font-semibold mb-4 text-sm">Average ORP</h6>
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-bold">
                    <span>{averageOrpValue} mV</span>
                  </h3>
                  <p>ORP Value</p>
                  <p>Last 24 hours</p>
                </div>
                {averageOrpValue !== 0 && (
                  <div className="relative group">
                    <div 
                      className="w-8 h-8 rounded-full border border-gray-200" 
                      style={{ backgroundColor: averageOrpColor }}
                    />
                    <div className="absolute bottom-full right-0 mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10">
                      <p>ORP value: {averageOrpValue} mV</p>
                      <p>Range: 0-1400 mV</p>
                      <p>Ideal range: 672-728 mV (48-52%)</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-4">
          {timeSeriesData.length > 0 ? (
            <div className="card card-border">
              <div className="card-body">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4>ORP Values Over Time</h4>
                    <p>Monitor water oxidation-reduction potential fluctuations</p>
                  </div>
                </div>
                
                {loading && (
                  <div className="flex justify-center items-center h-64">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                )}
                
                {error && (
                  <div className="alert alert-danger">
                    {error}
                  </div>
                )}
                
                {!loading && !error && (
                  <TimeSeriesChart
                    deviceId={deviceId}
                    startTime={formattedStartTime}
                    endTime={formattedEndTime}
                    interval={interval}
                    height={500}
                    data={timeSeriesData}
                    showTitle={false}
                    onIntervalChange={handleIntervalChange}
                    onDateRangeChange={handleDateRangeChange}
                    metricKey="orp_value"
                    yAxisTitle="ORP (mV)"
                    tooltipValueSuffix=" mV"
                    seriesName="ORP Value (mV)"
                    chartColor="#8b5cf6"
                    yAxisAnnotations={desiredOrpAnnotations}
                    title="ORP Values"
                    subtitle="Monitor oxidation-reduction potential over time"
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="flex justify-center items-center h-64">
              <p>No data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrpValuePage; 
